import { Injectable } from '@angular/core';
import {  Observable, of} from 'rxjs';
import { map, tap} from 'rxjs/operators'
import { ERequestTypes, ERequestCategory, TRequestCategory } from '@modweb5/sharedEnums/requestTypes.enum';
import { UserService } from '@modweb5/sharedServices/userService/user.service';
import { ObservableService } from '@modweb5/sharedServices/observableService/observable-service.service';
import { TServerResponse } from '@modweb5/interfaces/serverResponse.interface';

import { TMastersModule } from '@modweb5/interfaces/modules.interface';
import { TItemsFromForeignEntities } from '@modweb5/interfaces/moduleParams.interface';
import { TRecordTable } from '@modweb5/interfaces/todo.interface';

import { DataHttpService } from './data-http.service';
import {environment} from '@modweb5/environments/frontends/environment';

// Datové entity
//  - workers
//  - firms
//  - users
//  - projects
//  - projectWorkers
//  - workerProjects
//  - works
//  - history
//  - abonents
//  - magazines


@Injectable({
  providedIn: 'root'
})
export class DataGateService {


	// dataStorageEntityMap - zde jsou uloženy v Map všechny záznamy všech entit. 
	// Jakýkoliv záznam z jakékoliv entity lze jednoduše získat pomocí funkce getOneRecordFromDataMap
	private dataStorageEntityMap: Map<string, Map<number | string, any>>;

	private lastDataSystem: any = [];
	private timestamp = 'start';
	private tableRecordsCountLimits: Map<string, number>  = new Map();

	private responseDatabaseFilesForImport: any = null;
	
	watermarkConfig: any;
	changedLastWorkDates: Map<number, string> = new Map();
	lacDBNextDatabaseId: string;  // ID, které musí být pro databáze lakoven jedinečné.

	private userAccessRightsMap: Map<string, any> = new Map();

  constructor(
		private observableService: ObservableService,
		private _userService: UserService,
		private dataHttpService: DataHttpService

	) {
		this.clearAllData();
	}


	// ----------------------------------------------------------------------------------------------
	clearAllData() {
	// Inicializace prázdných dat jednotlivých entit
	// ----------------------------------------------------------------------------------------------

		this.lastDataSystem = [];
		this.watermarkConfig = {};
		this.timestamp = 'start';
		this.dataStorageEntityMap = new Map();
		if (environment.projectGroup === 'lacDB') {
			this.dataStorageEntityMap.set('projectGroupLacDB', new Map());
		}
	}

	//-----------------------------------------------------------------------------------------------
	checkUserAuthentication$(): Observable<boolean> {
	// ----------------------------------------------------------------------------------------------
		// Ptá se serveru, zda je uživatel přihlášen.
		const params = [{ token: 'checkUserAuthentication' }];
		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			tap(response => {
				this.applyChangesFromServer(response);
			}),
			map(data => {
				if (data.system.error !== 'no') {
					alert(data.system.error_message);
				}
				this.setUserAccessRightsMap(data.system);
				this._userService.setUser(data.system.user);
				this.observableService.emitSystemInfo(data.system);
				return data.system.user.isLogin;
			}),
		)
	}

	// ----------------------------------------------------------------------------------------------
	private getTokensForLacDatabase(aTokensRequest, tokensExistAndNonExist): any {
	// Vrátí existující a neexistující tokeny pro entity v dané databázi

		const databaseMap = this.dataStorageEntityMap.get('projectGroupLacDB');
		aTokensRequest.forEach((item) => { 

			if (this.isStandardToken(item.token)) {
				if (this.dataStorageEntityMap.has(item.token)) {
					tokensExistAndNonExist.exists.push(item);
				} else {
					tokensExistAndNonExist.nonExists.push(item);
				}
			} else {
				if (databaseMap.has(item.databaseID)) {
					// Pokud už nějaká data byla pro tuto databázi uložena do dataStorageEntityMap, 
					// tak si vytáhni všechna data uložená pro tuto databázi.
					const tokensForDatabase = databaseMap.get(item.databaseID);
					if (tokensForDatabase.has(item.token)) {
						// Pokud byl pro tuto databázi uložen daný token dat, tak token už existuje.
						tokensExistAndNonExist.exists.push(item);
					} else {
						// Jinak neexistuje.
						tokensExistAndNonExist.nonExists.push(item);
					}
				} else {
					// Pokud nebyly uloženy žádná data pro databázi, neexistují data ani pro daný token.
					tokensExistAndNonExist.nonExists.push(item);
				}
			}
		})
		return tokensExistAndNonExist;
	}

	// ----------------------------------------------------------------------------------------------
	isStandardToken(aTokenName: string) {
		return ((environment.projectGroup === 'standard') || 
						(aTokenName === 'lacDatabases') || 
						(aTokenName === 'checkUserAuthentication'));
	}

	// ----------------------------------------------------------------------------------------------
	requestForDataNew$(aTokens: Array<any>): Observable<TServerResponse> {

		// Server požadáme pouze o data, která pro daný token zatím neexistují.
		// Vytvoříme pole prarametrů již existujícími a daty a s ještě neexistujícími daty.
		let tokens = { exists: [], nonExists: [] };

		if ((environment.projectGroup === 'standard')) {
			aTokens.forEach((item) => { 
				if (this.dataStorageEntityMap.has(item.token)) {
					tokens.exists.push(item);
				} else {
					tokens.nonExists.push(item);
				}
			})
		} else if (environment.projectGroup === 'lacDB') {
			tokens = this.getTokensForLacDatabase(aTokens, tokens);
		}

		// Pokud nějaká data ještě neexistují, zeptáme se na ně serveru.
		if (tokens.nonExists.length > 0) {
			const existTokens = this.setExistTokenForHeader();
			const request = this.dataHttpService.processServerRequest$(ERequestTypes.GET, null, tokens.nonExists, existTokens, this.timestamp).pipe(
				tap(response => {
					this.applyChangesFromServer(response);
				}),
				map(response => {
					// console.log('response');
					// console.log(JSON.stringify(response));
					if (response.system['e2e-test'] === 'yes') {
						window['orax-e2e-server-system'] = response.system;
						window['orax-e2e-server-data'] = response.data;
					}
					if (response.system.error !== 'no') {
						alert(response.system.error_message);
					} else {
						for (const key in response.system.table_limits) {
							this.tableRecordsCountLimits.set(key, response.system.table_limits[key]);
						}
						this.observableService.emitIsDemo(response.system.isDemo);

						// Nastavíme dosud neexistující data do již existujících
						tokens.nonExists.forEach((item, idx) => { 

							if (item.token === 'photogallery') {
								// Pokud se posílají fotogalerie, posílá se automaticky konfigurace pro vytváření vodoznaku.
								this.watermarkConfig = response.data['watermarkConfig'];
							}
							if (item.token === 'accessRightValues') {
								this.setDataStorageEntityMapForOneEntityCommon(item, 'accessRightList', response.data['accessRightList']);
							}
							if (item.token === 'lacDatabases') {
								this.lacDBNextDatabaseId = response.data['lacDBNextDatabaseId'];
							}
							this.setDataStorageEntityMapForOneEntityCommon(item, item.token, response.data[item.token]);
							this.setUserAccessRightsMap(response.system);

							// Toto by se nemělo stát
							// if (!this.dataStorageEntityMap.has(item.token)) {
							//	this.dataStorageEntityMap.set(item.token, new Map());
							// }

							// Toto je snad zbytečné, protože this.dataStorageEntityMap se už nastavila ve funkci
							// setDataStorageEntityMapForOneEntityCommon
							// const dataStorageEntityMap = this.dataStorageEntityMap.get(item.token);
							// response.data[item.token].forEach(record => {
								// dataStorageEntityMap.set(record.id, record);
							// })
							// this.dataStorageEntityMap.set(item.token, dataStorageEntityMap);
						});

						// Nastavíme data došlá v sekci systém, abychom je pak mohli nastavit stejná (mimo duration),
						// až se na data nebudeme ptát severu.
						this.setLastDataSystem(response.system);

						// Doplníme do došlých dat ze serveru, požadovaná data, která již byla na klientovi 
						// (nevrátily se ze serveru).
						tokens.exists.forEach((item) => {
							response.data[item.token] = this.getArrayAllRecordsFromOneEntityCommon(item);
						});
					}
					return response;
				}),
				tap(rawData => {
					// console.log('vracím data');
					// console.log(rawData);
				}),
			);
			return request;
		} else {
			this.extendUserLogin$().subscribe(
				user => {
					if (!user['isLogin']) {
						this._userService.setUser(user);	
					}
				}
			)

			let data = {};
			aTokens.forEach((item) => {
				data[item.token] = this.getArrayAllRecordsFromOneEntityCommon(item);
			});

			// Doplníme o část system (s duration 0,000 sec)
			let system = this.lastDataSystem;
			// Vytvoříme observable pomocí operátoru of a vrátíme výsledek.
			const response = { data, system }
			return of(response);
		}
	}

	//-----------------------------------------------------------------------------------------------
	setDatabaseStatisticalInformation$(databaseID: string | number): Observable<any> {

		const params = [{ "fcnRequest": "setDatabaseStatisticalInformation", "databaseID": databaseID }];
		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(
										ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			map(data => {
				if (data.system.error === 'no') {
					this.setStatisticInfo(databaseID, data.data.databaseStatisticalInformation);
				}
				return data;
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	private setStatisticInfo(databaseID: string | number, databaseStatisticalInformation: any) {

		const lacDatabases = this.dataStorageEntityMap.get('lacDatabases'); 
		const oneDB = lacDatabases.get(databaseID); 
		oneDB.calculationDateTime = databaseStatisticalInformation.calculationDateTime;
		oneDB.calculationDateTime_sort = databaseStatisticalInformation.calculationDateTime_sort;
		oneDB.oldestDateM = databaseStatisticalInformation.oldestDateM;
		oneDB.oldestDateM_sort = databaseStatisticalInformation.oldestDateM_sort;
		oneDB.newestDateM = databaseStatisticalInformation.newestDateM;
		oneDB.newestDateM_sort = databaseStatisticalInformation.newestDateM_sort;
		oneDB.recordCountM = databaseStatisticalInformation.recordCountM;
		oneDB.oldestDateT_sort = databaseStatisticalInformation.oldestDateT_sort;
		oneDB.recordCountT = databaseStatisticalInformation.recordCountT;
		lacDatabases.set(databaseID, oneDB); 
		this.dataStorageEntityMap.set('lacDatabases', lacDatabases); 
	}


	// ----------------------------------------------------------------------------------------------
	getLacDBNextDatabaseId(): string {
	// ----------------------------------------------------------------------------------------------
		return this.lacDBNextDatabaseId;
	}

	// ----------------------------------------------------------------------------------------------
	private setUserAccessRightsMap(responseSystem) {
	// ----------------------------------------------------------------------------------------------
		if (typeof responseSystem.user.id !== typeof undefined) {
			this.userAccessRightsMap.set('userInfo', {'id': responseSystem.user.id, 'hierarchy': responseSystem.user.hierarchy });
		}
		if (typeof responseSystem.access_rights !== typeof undefined) {
			let key;
			for (key in responseSystem.access_rights) {
				this.userAccessRightsMap.set(key, responseSystem.access_rights[key]);
			}
		}
		if (typeof responseSystem.user.moduleAccess !== typeof undefined) {
			let moduleAccessRightsMap: Map<string, string> = new Map();

			let moduleId;
			for (moduleId in responseSystem.user.moduleAccess) {
				moduleAccessRightsMap.set(moduleId, responseSystem.user.moduleAccess[moduleId]);
			}
				this.userAccessRightsMap.set('moduleAccess', moduleAccessRightsMap);
		}
	}

	// ----------------------------------------------------------------------------------------------
	getUserAccessRightsMap(key): any {
	// ----------------------------------------------------------------------------------------------

		return this.userAccessRightsMap.get(key);
	}

	// ----------------------------------------------------------------------------------------------
	getUserHasAccessToModule(aModuleId): any {
	// ----------------------------------------------------------------------------------------------

		const accessToModules =  this.userAccessRightsMap.get('moduleAccess');
		const accessToOneModule = accessToModules.get(aModuleId);
		return (accessToOneModule === 'x');
	}

	// ----------------------------------------------------------------------------------------------
	private applyChangesFromServer(response) {
	// ----------------------------------------------------------------------------------------------

		if ((typeof response.system.timestamp !== typeof undefined) && 
				(environment.projectGroup === 'standard')) {
			let tokenName;
			let keyRecord;
			for (tokenName in response.data.deletedData) {
				let pDataStorageEntityMap = this.dataStorageEntityMap.get(tokenName); 
				for (keyRecord in response.data.deletedData[tokenName]) {
					const record = response.data.deletedData[tokenName][keyRecord];
					pDataStorageEntityMap!.delete(record.id);
				}
				this.dataStorageEntityMap.set(tokenName, pDataStorageEntityMap); 
			}

			for (tokenName in response.data.changedData) {
				let pDataStorageEntityMap = this.dataStorageEntityMap.get(tokenName); 
				for (keyRecord in response.data.changedData[tokenName]) {
					const record = response.data.changedData[tokenName][keyRecord];
					pDataStorageEntityMap!.set(record.id, record);
				}
				this.dataStorageEntityMap.set(tokenName, pDataStorageEntityMap); 
			}
			this.timestamp = response.system.timestamp;
		}
	}


	//-----------------------------------------------------------------------------------------------
	getTableRecordsCountLimit(aTableName): number {
	// ----------------------------------------------------------------------------------------------
		let resultFcn = -1;
		if (this.tableRecordsCountLimits.get(aTableName)) {
			resultFcn = this.tableRecordsCountLimits.get(aTableName);
		}
		return resultFcn;
	}

	// ----------------------------------------------------------------------------------------------
	private setDataStorageEntityMapForOneEntityCommon(item: any, tokenName: string, entityData: Array<any>): void {
	// Nastaví všechny data entity (jako pole záznamů) do dataStorageEntityMap jako mapu záznamů

		if ((environment.projectGroup === 'standard') || (this.isStandardToken(tokenName))) {
			this.setDataStorageEntityMapForOneEntity(tokenName, entityData);
		} else if (environment.projectGroup === 'lacDB') {
			this.setDataStorageEntityMapForDatabaseForOneEntity(item, tokenName, entityData, false);
		}
	}

	// ----------------------------------------------------------------------------------------------
	private setDataStorageEntityMapForDatabaseForOneEntity(item: any, tokenName: string, 
			entityData: Array<any>, changeDataOnlyIfExists: boolean): void {
	// Nastaví všechny data entity (jako pole záznamů) do dataStorageEntityMap jako mapu záznamů

		let entityDataMap = new Map();
		entityData.forEach((record) => {
			entityDataMap.set(record.id, record);
		});

		const databaseMap = this.dataStorageEntityMap.get('projectGroupLacDB');
		let oneDatabaseMapTokens;
		if (databaseMap.has(item.databaseID)) {
			oneDatabaseMapTokens = databaseMap.get(item.databaseID);
		} else {
			oneDatabaseMapTokens = new Map();
		}
		if ((!changeDataOnlyIfExists) || ((changeDataOnlyIfExists) && (oneDatabaseMapTokens.has(tokenName))))  {
			oneDatabaseMapTokens.set(tokenName, entityDataMap);
			databaseMap.set(item.databaseID, oneDatabaseMapTokens);
			this.dataStorageEntityMap.set('projectGroupLacDB', databaseMap);
		}
	}


	// ----------------------------------------------------------------------------------------------
	private setDataStorageEntityMapForOneEntity(tokenName: string, entityData: Array<any>): void {
	// Nastaví všechny data entity (jako pole záznamů) do dataStorageEntityMap jako mapu záznamů

		let entityDataMap = new Map();
		entityData.forEach((record) => {
			entityDataMap.set(record.id, record);
		});
		this.dataStorageEntityMap.set(tokenName, entityDataMap); 
	}

	// ----------------------------------------------------------------------------------------------
	public changeFieldsInOneRecordInOneEntity(tokenName: string, id: string | number, fields: Array<any>): void {
	// Funkce změní v uložených datech předané položky v jednom záznamzu dané entity.
	// Používá se při volání nějaké funkce, která u záznamu nastaví například jiný status a ten se musí hned změnit
	// v přehledu zýznamů (v zobrazené tabulce).

		// Ze store Vezmeme celou tabulku.
		const entityData = this.dataStorageEntityMap.get(tokenName); 
		if (entityData.has(id)) {
			// Z  tabulky vytáhneme záznam.
			const record = entityData!.get(id);
			fields.forEach(field => {
				// V záznamu změníme zaslané hodnoty.
				record[field['name']] = field['value'];
			});
			// Změněný záznam uložíme do tabulky.
			entityData.set(id, record);
			// Tabulku uložíme zpět do store.
			this.dataStorageEntityMap.set(tokenName, entityData); 
		}
	}

	//-----------------------------------------------------------------------------------------------
	getEntityRecordCount(tokenName): number {
	// Vrátí počet záznamů v entitě

		let pResult = 0;
		if ((environment.projectGroup === 'standard') || (this.isStandardToken(tokenName))) {
			if (this.dataStorageEntityMap.has(tokenName)) {
				const entityMap = this.dataStorageEntityMap.get(tokenName); 
				pResult = entityMap!.size;
			}
		}
		return pResult;
	}


	//-----------------------------------------------------------------------------------------------
	getOneRecordFromDataMap(tokenName: string, id, itemsFromForeignEntities: TItemsFromForeignEntities): TRecordTable | null {
	// Vrátí jeden záznam z dané entity jako objekt

		let record = null;
		if (this.dataStorageEntityMap.has(tokenName)) {
			const entityMap = this.dataStorageEntityMap.get(tokenName); 
			if (entityMap!.has(id)) {
				record = entityMap!.get(id);
				record = this.addOtherData(record, itemsFromForeignEntities, 3)
			}
		}
		return JSON.parse(JSON.stringify(record));
	}

	//-----------------------------------------------------------------------------------------------
	getOneRecordFromDataMapForLac(databaseID: string, tokenName: string, id, 
													itemsFromForeignEntities: TItemsFromForeignEntities): TRecordTable | null {
	// Vrátí jeden záznam z dané entity jako objekt

		let record = null;

		const databaseMap = this.dataStorageEntityMap.get('projectGroupLacDB');
		if (databaseMap.has(databaseID)) {
			const databaseMapTokens = databaseMap.get(databaseID);
			if (databaseMapTokens.has(tokenName)) {
				const entityRecordsMap = databaseMapTokens.get(tokenName)
				if (entityRecordsMap.has(id)) {
					record = entityRecordsMap!.get(id);
				}
			}
		}
		return JSON.parse(JSON.stringify(record));
	}

	//-----------------------------------------------------------------------------------------------
	private getArrayAllRecordsFromOneEntityCommon(item: any):  Array<any> {
		if ((environment.projectGroup === 'standard') || (this.isStandardToken(item.token))) {
			return this.getArrayAllRecordsFromOneEntity(item.token);
		} else if (environment.projectGroup === 'lacDB') {
			return this.getArrayAllRecordsForDatabaseFromOneEntity(item);
		}
	}

	//-----------------------------------------------------------------------------------------------
	getArrayAllRecordsForDatabaseFromOneEntity(item: any):  Array<any> {
	// Vrací entitu pro vybranou databázi lakovny
		let pResult: Array<any> = [];

		const databaseMap = this.dataStorageEntityMap.get('projectGroupLacDB');
		if (databaseMap.has(item.databaseID)) {
			const databaseMapTokens = databaseMap.get(item.databaseID);
			if (databaseMapTokens.has(item.token)) {
				const entityRecordsMap = databaseMapTokens.get(item.token)
				entityRecordsMap!.forEach(record => {
					pResult.push(JSON.parse(JSON.stringify(record)));
				});
			}
		}
		return pResult
	}


	//-----------------------------------------------------------------------------------------------
	getArrayAllRecordsFromOneEntity(tokenName):  Array<any> {
	// Vrátí záznamy z dané entity jako pole záznamů (vezmou si je z mapy záznamů)
		let pResult: Array<any> = [];
		const entityRecordsMap = this.dataStorageEntityMap.get(tokenName); 
		entityRecordsMap!.forEach(record => {
			pResult.push(JSON.parse(JSON.stringify(record)));
		});
		return pResult
	}

	// ----------------------------------------------------------------------------------------------
	private setOneRecordToDataStorageEntityMap(tokenName: string, record: any): void {
	// Nastaví záznam do mapy záznamů, která je uložena v mapě entit 

		if ((environment.projectGroup === 'standard') || (this.isStandardToken(tokenName))) {
			// Vytáhne si mapu záznamů z mapy entit
			const entityRecordsMap = this.dataStorageEntityMap.get(tokenName);
			// Nastaví do mapy záznamů daný záznam
			entityRecordsMap!.set(record.id, JSON.parse(JSON.stringify(record)));
			// Mapu záznamů uloží zpět do mapy entit
			this.dataStorageEntityMap.set(tokenName, entityRecordsMap);
		} else {

			// Vezmeme si mapu dat pro jednotlivé databáze ze sekce určené pro lakovny
			const databasesMap = this.dataStorageEntityMap.get('projectGroupLacDB');

			let oneDatabaseMap;
			let oneTokenForOneDatabaseMap;

			// Vezmeme si nebo vytvoříme mapu tokenů pro naši databázi.
			if (databasesMap.has(record.databaseID)) { 
				oneDatabaseMap = databasesMap.get(record.databaseID);
			} else {
				oneDatabaseMap = new Map();
			}

			// Vezmeme si nebo vytvoříme mapu záznamů pro náš token v dané databázi
			if (oneDatabaseMap.has(tokenName)) { 
				oneTokenForOneDatabaseMap = oneDatabaseMap.get(tokenName);
			} else {
				oneTokenForOneDatabaseMap = new Map();
			}
			// Uložíme zaslaný záznam do mapy našeho tokenu
			oneTokenForOneDatabaseMap.set(record.id, record);
			// Uložíme mapu našeho token do mapy pro naši databázi
			oneDatabaseMap.set(record.databaseID, oneTokenForOneDatabaseMap);
			// Uložíme mapu naší databáze do sekce určené pro lakovny
			this.dataStorageEntityMap.set('projectGroupLacDB', oneDatabaseMap);
		}
	}

	//-----------------------------------------------------------------------------------------------
	private insertRecord(tokenName, record): void {
	// ----------------------------------------------------------------------------------------------
		this.setOneRecordToDataStorageEntityMap(tokenName, record);
	}

	//-----------------------------------------------------------------------------------------------
	updateRecord(tokenName, record): void {
	// ----------------------------------------------------------------------------------------------
		this.setOneRecordToDataStorageEntityMap(tokenName, record);
	}

	//-----------------------------------------------------------------------------------------------
	private deleteRecord(tokenName, body): void {
	// ----------------------------------------------------------------------------------------------

		if (this.isStandardToken(tokenName)) {
			this.deleteRecordStd(tokenName, body.id);
		} else {
			this.deleteRecordLac(body.databaseID, tokenName, body.id);
		}
	}

	//-----------------------------------------------------------------------------------------------
	private deleteRecordStd(tokenName, recordID): void {
	// ----------------------------------------------------------------------------------------------

		const entityRecordsMap = this.dataStorageEntityMap.get(tokenName); 
		// Nastaví do mapy záznamů daný záznam
		entityRecordsMap!.delete(recordID);
		// Mapu záznamů uloží zpět do mapy entit
		this.dataStorageEntityMap.set(tokenName, entityRecordsMap);
	}

	//-----------------------------------------------------------------------------------------------
	private deleteRecordLac(databaseID, tokenName, recordID): void {	
	// ----------------------------------------------------------------------------------------------
		// Vytáhneme mapu dat pro lakovny z mapy všech dat 
		const databasesMap = this.dataStorageEntityMap.get('projectGroupLacDB');
		if (databasesMap.has(databaseID)) {
			// Vytáhneme mapu jedné databáze z mapy pro lakovny
			let oneDatabaseTokensMap = databasesMap.get(databaseID);
			if (oneDatabaseTokensMap.has(tokenName)) {
				// Vytáhneme mapu pro jeden token (tabulku) z mapy jedné databáze
				const oneTokensMap = oneDatabaseTokensMap.get(tokenName); 
				// V mapě jednoho tokenu zrušíme daný záznam
				oneTokensMap!.delete(recordID);
				// Nasetujeme mapu jednoho tokenu zpět do mapy jedné databáze
				oneDatabaseTokensMap.set(tokenName, oneTokensMap);
				// Nasetujeme mapu jedné databáze zpět do mapy lakoven
				databasesMap.set(databaseID, oneDatabaseTokensMap);
				// Nasetujeme mapu lakoven dat pro lakovny do mapy všech dat
				this.dataStorageEntityMap.set('projectGroupLacDB', databasesMap);
			} 
		} 
	}


	//-----------------------------------------------------------------------------------------------
	getDataForChildModule$(dataParams: any): Observable<any> {
	// Pro lakovny nejsou načteny podřízené záznamy ke všem databázím. Proto vždy zavoláme 
	// requestForDataNew$, tam se zjistí, jestli daný token pro danou lakovnu již existuje.
	// Pokud ano, vrátí se z paměti, pokud ne, zeptá se serveru a uloží ho do paměti.
	// ----------------------------------------------------------------------------------------------

		return this.requestForDataNew$([{ "token": dataParams.tokenName, "databaseID": dataParams.databaseID }]);
	}


	// ----------------------------------------------------------------------------------------------
	public getDataForChildModule(dataParams: any): any {


	// Vrací data pro modul a podle nastavení filterIds (idéčka nadřízených záznamů) filtruje data pro child modul
	// otherData - data, která se k záznamu doplňují z jiných entit
	//  tokenName - 

	// Příklad dataParams
	// {
	// 		token: 'projectWorkers',  // jméno entity, z které se vrací data
	// 		filterIds: [              // filtr, který musí splňovat záznam, aby byl vybrán
	// 			{childKeyName: "projectId", childKeyValue: 10}, // jeho položka projectId musí být rovna 10
	// 			{childKeyName: "workerId", childKeyValue: 1}    // a současně jeho položka workerId musí být rovna 1
	// 		 ]
	// 		otherData: [
	// 			{
	// 				tokenName: 'workers', // jméno entity, z které se data doplňují do původního záznamu
	// 				keyFieldName: projectId',  // jméno položky v původním záznamu, kde je uložen klíč pro vyhledání záznamu v Map entity, z které se doplňuje.
	// 				fields: [ 'name', 'surname' ] // jména doplňovaných položek v entitě, z které se doplňuje.
	// 			}
	// 		]
	// }

	// ----------------------------------------------------------------------------------------------

		let resultFcn = [];
		const isFilter = (dataParams.filterIds.length > 0);
		const isOtherData = (dataParams.otherData.length > 0);
		// Někde se používá token a někde tokenName - tady potřebuji, aby tam byl token - mělo by se předělat (TODO), ať všude je token
		dataParams.token = dataParams.tokenName;

		const	records = this.getArrayAllRecordsFromOneEntityCommon(dataParams);

		records.forEach(record => {
			if (isFilter) {
				if (this.passThroughFilter(dataParams.filterIds, record)) {
					if (isOtherData) {
						record = this.addOtherData(record, dataParams.otherData, 1)
					}
					resultFcn.push(record);
				}
			} else {
				record = this.addOtherData(record, dataParams.otherData, 2)
				resultFcn.push(record);
			}
		});
		return resultFcn;
	}

	//-----------------------------------------------------------------------------------------------
	private addOtherData(record, otherData, a): any {
	// Přidá k záznamu jedné entity položky z jiné entity
	// Například k vazbám pracovníků na projekty (entita projectWorkers) přidá jméno a příjmení 
	// pracovníka (z entity workers).

	// record - původní záznam, ke kterému se přidávají položky
	// Příklad otherData
	//otherData: [
	// 			{
	// 				tokenName: 'workers',
	// 				keyFieldName: projectId',
	// 				fields: [ 'name', 'surname' ]
	// 			}
	// 		]
	// ----------------------------------------------------------------------------------------------

		// Projde názvy všech entity z kterých se mají položky doplňovat.
		otherData.forEach(fromEntity => {
			const otherRecord = this.getOneRecordFromDataMap(fromEntity.tokenName, record[fromEntity.keyFieldName], []);
			// Projde v záznamu všechny položky, které se mají doplnit
			fromEntity.fields.forEach(field => {
				// a doplní položky
				if (typeof otherRecord !== typeof undefined) {
					record[field] = otherRecord[field];
				} else {
					record[field] = '';
				}

			});
		});
		return record;
	}

	//-----------------------------------------------------------------------------------------------
	isMultiFirms$(): Observable<boolean> {
	// ----------------------------------------------------------------------------------------------
		// Ptá se serveru, jestli se systém používá jako multifiremní.

		const params = [{ token: 'isMultiFirms' }];
		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			tap(response => {
				this.applyChangesFromServer(response);
			}),
			map(data => {
				if (data.system.error !== 'no') {
					alert(data.system.error_message);
				}
				return data.system.multi_firm === 'yes';
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	dataRefresh$(): Observable<TServerResponse> {
	// ----------------------------------------------------------------------------------------------
	// Ze serveru se vrátí pouze data, která byla opravena nebo přidána a info o zrušení záznamů 

		const params = [{ token: 'dataRefresh' }];
		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			tap(response => {
				this.applyChangesFromServer(response);
			}),
			map(response => {
				return response;
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	saveData$(body: any, params: Array<any>): Observable<any> {
	// ----------------------------------------------------------------------------------------------
		let reqType = ERequestTypes.PUT;

		if (typeof params[0]['reqType'] !== typeof undefined) {
			reqType = params[0]['reqType'];
			delete params[0]['reqType'];
		}

		params[0]['function'] = ((params[0]['function'] === 'INSERTPREF') ? 'INSERT' : params[0]['function']);

		let requestCategory: TRequestCategory;
		let key;
		for (key in params[0]) {
			if ((key === 'token') || (key === 'fcnRequest')) {
				requestCategory = key;
			}
		}

		const existTokens = this.setExistTokenForHeader();
		const request = this.dataHttpService.processServerRequest$(reqType, body, params, existTokens, this.timestamp).pipe(
			tap(rawData => {

				if (rawData.system['e2e-test'] === 'yes') {
					window['orax-e2e-server-system'] = rawData.system;
					window['orax-e2e-server-data'] = rawData.data;
				}
				if (requestCategory === ERequestCategory.token) {
					this.applyChangesFromServer(rawData);
					if ((rawData.system.error === 'no') && (!rawData.data[params[0]['token']].wasError)) {
						if (params[0]['function'] === 'INSERT') {
							this.insertRecord(
								params[0]['token'],
								rawData.data[params[0]['token']].currentRecord);
						} else if (params[0]['function'] === 'UPDATE') {
							this.updateRecord(
								params[0]['token'],
								rawData.data[params[0]['token']].currentRecord);
						} else if (params[0]['function'] === 'DELETE') {
							this.deleteRecord(params[0]['token'], body);
						}
					}

					this.changeParentData(rawData.data, params[0]['token']);

				} else if (requestCategory === ERequestCategory.fcnRequest) {
					if (params[0].fcnRequest === 'fcnSetXLS') {
						const newData = JSON.parse(JSON.stringify(rawData.data.fcnSetXLS.newData));
						for (const key in newData) {
							if (Object.prototype.hasOwnProperty.call(newData, key)) {
								const value = newData[key];
								const pItem = {token: key, databaseID: rawData.data.fcnSetXLS.databaseID}
								this.setDataStorageEntityMapForDatabaseForOneEntity(pItem, key, value, false);
							}
						}
					} else if (params[0].fcnRequest === 'fcnSetExcelAlarms') {
						const newData = JSON.parse(JSON.stringify(rawData.data.fcnSetExcelAlarms.newData));
						for (const key in newData) {
							if (Object.prototype.hasOwnProperty.call(newData, key)) {
								const value = newData[key];
								const pItem = {token: key, databaseID: rawData.data.fcnSetExcelAlarms.databaseID}
								this.setDataStorageEntityMapForDatabaseForOneEntity(pItem, key, value, false);
							}
						}
					}
				}
				return rawData;
			}),
		);
		return request;
	}

	// ----------------------------------------------------------------------------------------------
	private changeParentData(data, token) {
	// ----------------------------------------------------------------------------------------------
		if ((token === 'customerApps') && (data.customers)) {
			const parentRecord = this.getOneRecordFromDataMap('customers', data.customers['customid'], []);
			parentRecord['activesw'] = data.customers['activesw'];
			this.setOneRecordToDataStorageEntityMap('customers', parentRecord);
			this.observableService.emitRecordInModuleWasChanged({moduleId: 'customers', id: data.customers['customid']});
		}
	}

	// ----------------------------------------------------------------------------------------------
	private setLastDataSystem(lastDataSystem) {
	// ----------------------------------------------------------------------------------------------
		this.lastDataSystem = JSON.parse(JSON.stringify(lastDataSystem));
		this.lastDataSystem.duration = '0,000 sec';
	}

	// ----------------------------------------------------------------------------------------------
	private passThroughFilter(filterIds, record): boolean {
	// ----------------------------------------------------------------------------------------------

		let resultFcn = true;
		filterIds.forEach(filter => {
			if (record[filter['childKeyName']] !== filter['childKeyValue']) {
				resultFcn = false;
			}
		});
		return resultFcn;
	}

	//-----------------------------------------------------------------------------------------------
	private setExistTokenForHeader(): string {
	// ----------------------------------------------------------------------------------------------

		let pResult = '';
		this.dataStorageEntityMap.forEach((record, key) => {
			if (this.isStandardToken(key)) {
				if (pResult !== '') {
					pResult += ',';
				}
				pResult += key;
				
			}
		});
		return pResult;
	}

	//-----------------------------------------------------------------------------------------------
	extendUserLogin$(): Observable<any> {
	// ----------------------------------------------------------------------------------------------
		// Ptá se serveru, zda je uživatel přihlášen.
		const params = [{ token: 'extendUserLogin' }];
		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			tap(response => {
				this.applyChangesFromServer(response);
			}),
			map(data => {
				if (data.system.error !== 'no') {
					alert(data.system.error_message);
				}
				return data.system.user;
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	getLabelForMasterModules(aMasterModules:Array<TMastersModule>, aLang: string): string {

		let subtitle = '';

		if (environment.projectGroup === 'standard') {
			aMasterModules.forEach(moduleInfo => {
				let recordsMap = this.dataStorageEntityMap.get(moduleInfo.moduleId);
				let record = recordsMap.get(moduleInfo.id)
				if (typeof moduleInfo.foreignEntity !== typeof undefined) {
					recordsMap = this.dataStorageEntityMap.get(moduleInfo.foreignEntity);
					const keyValue = record[moduleInfo.foreignKeyFieldName];
					record = recordsMap.get(keyValue);
				}
				let subtitleModule = '';
				moduleInfo.labelFields.forEach(fieldName => {
				if (subtitleModule !== '') {
						subtitleModule += ((typeof moduleInfo.labelFieldsDelimiter !== typeof undefined) ? 
																																moduleInfo.labelFieldsDelimiter : ' ');
					}
					subtitleModule += record[fieldName];
				});
				if (subtitle !== '') {
					subtitle += ', ';
				}
				subtitle += moduleInfo.labels[aLang] + ' "' + subtitleModule + '"';
			});
		}
		return subtitle;
	}

	//-----------------------------------------------------------------------------------------------
	getRecordsForMasterModules(aMasterModules:Array<TMastersModule>): Array<any> {
		// Vrátí aktuální záznam z každého master modulu.
		// Při vnoření na 3. úroveň vrátí dva záznamy (jeden z nejvyšší úrovně a jeden z nižší úrovně)

		let records = []
			aMasterModules.forEach(moduleInfo => {
				if ((environment.projectGroup === 'standard') || (this.isStandardToken(moduleInfo.moduleId))) {
					let recordsMap = this.dataStorageEntityMap.get(moduleInfo.moduleId);
					records[moduleInfo.moduleId] = recordsMap.get(moduleInfo.id);
				}
			});
		return records;
	}

	//-----------------------------------------------------------------------------------------------
	setChangedLastWorkDates(projectId: number, newDate: string): void {
	// Uschová poslední zadané datum práce k danému id projektu.
	// Při výběru projektu do práce se projekty třídí tak, aby byly vepředu ty, pro které přihlášený	
	// pracovník prováděl práci naposled. Tato informace se na začátku přitáhla ze serveru.
	// Po uložení práce na serveru se nový projekt s novým posledním datem na klienta neposílá,
	// protože fyzicky nedošlo ke změně záznamu projektu (ukládala se práce)
	// Aby uživatel nemusel provádět refresh, ukládá se zadané datum. Toto datum pak přepíše datum,
	// které se nastavilo, když přišla data ze serveru.

		// Poslední datum práce se ukládá jenom, když byla práce prováděna později než odpovídá poslednímu uloženému datu
		const oldDate =	this.getChangedLastWorkDates(projectId);
		if (typeof oldDate === typeof undefined) {
			this.changedLastWorkDates.set(projectId, newDate);
		} else if (oldDate > newDate) {
			this.changedLastWorkDates.set(projectId, newDate);
		}
	}

	//-----------------------------------------------------------------------------------------------
	getChangedLastWorkDates(projectId: number) {

		return this.changedLastWorkDates.get(projectId);
	}

	//-----------------------------------------------------------------------------------------------
	private getE2ETestValue(): string {

		if (typeof window['ident-cypress-e2e'] === typeof undefined) {
			return 'no';
		} else {
			return window['ident-cypress-e2e'];
		}
	}

	//-----------------------------------------------------------------------------------------------
	exportEntireDatabase$(body: any, params: Array<any>): Observable<any> {
	// Pošle pouze ID databáze. Server na základě toho vytvoří soubory z parametrů měření, 
	// parametrů technologických časů a typů poruch.
	// ----------------------------------------------------------------------------------------------
		
		let reqType = ERequestTypes.POST;

			const existTokens = this.setExistTokenForHeader();
			const request = this.dataHttpService.processServerRequest$(reqType, body, params, existTokens, this.timestamp).pipe(
			tap(rawData => {
				return rawData;
			}),
		);
		return request;
	}

	//-----------------------------------------------------------------------------------------------
	importEntireDatabasesSendToServer$(databaseId: string | number, selectedFileName: string): Observable<any> {

		const params = [{
			"fcnRequest": "importEntireDatabaseSendToServer",
			"databaseID": databaseId,
			"selectedFileName": selectedFileName 
		}];
		const existTokens = this.setExistTokenForHeader();

		return this.dataHttpService.processServerRequest$(
										ERequestTypes.POST, null, params, existTokens, this.timestamp).pipe(
			tap(response => {
				// console.log('data-gate importEntireDatabasesSendToServer$');
				// console.log(JSON.stringify(response));
				if (response.system.error === 'no') {
				}
				return response;
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	importEntireDatabaseGetFiles$(): Observable<any> {
		if (this.responseDatabaseFilesForImport === null) {
			const params = [{ "fcnRequest": "importEntireDatabaseGetFiles" }];
			const existTokens = this.setExistTokenForHeader();

			return this.dataHttpService.processServerRequest$(
											ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
				tap(response => {
					if (response.system.error === 'no') {
						this.responseDatabaseFilesForImport = response;
					}
					return response;
				}),
			)
		} else {
			return of(this.responseDatabaseFilesForImport);
		}
	}
	//-----------------------------------------------------------------------------------------------
	getDatabaseAccesses$(databaseID: string | number): Observable<any> {

		const params = [{ "fcnRequest": "fcnGetAccesses", "databaseID": databaseID }];
		const existTokens = this.setExistTokenForHeader();

		return this.dataHttpService.processServerRequest$(
										ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			map(data => {
				if (data.system.error !== 'no') {
					alert(data.system.error_message);
				}
				return {
					userAccess: data.data.userAccess,
					demoAccess: data.data.demoAccess,
					controlUnitAccess: data.data.controlUnitAccess
				};
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	getTablesCount$(databaseID: string | number): Observable<any> {

		const params = [{ "fcnRequest": "fcnGetTablesCount", "databaseID": databaseID }];
		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(
										ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			map(data => {
				if (data.system.error !== 'no') {
					alert(data.system.error_message);
				}
				return data.data;
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	deleteOldDataFromOneDatabase$(data: any): Observable<any> {

		const params = [{ "fcnRequest": "fcnDeleteOldData", "data": data }];
		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(
										ERequestTypes.DELETE, null, params, existTokens, this.timestamp).pipe(
			map(response => {
				if (response.system.error === 'no') {
					this.setStatisticInfo(data['databaseID'], response.data.databaseStatisticalInformation);
				}
				return response;
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	checkDatabase$(databaseID: string | number): Observable<any> {

		const params = [{ "fcnRequest": "fcnDatabaseCheck", "databaseID": databaseID }];
		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(
										ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			map(data => {
				if (data.system.error !== 'no') {
					alert(data.system.error_message);
				}
				return data.data;
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	getCSVFile$(params: any): Observable<any> {

		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			tap(response => {
				this.applyChangesFromServer(response);
			}),
			map(data => {
				if (data.system.error !== 'no') {
					alert(data.system.error_message);
				}
				return data.data.fileContent;
			}),
		)
	}


	//-----------------------------------------------------------------------------------------------
	getFileContent$(fileName: string): Observable<any> {	

		const params = [{ file: fileName }];
		const existTokens = this.setExistTokenForHeader();
		return this.dataHttpService.processServerRequest$(ERequestTypes.GET, null, params, existTokens, this.timestamp).pipe(
			tap(response => {
				this.applyChangesFromServer(response);
			}),
			map(data => {
				if (data.system.error !== 'no') {
					alert(data.system.error_message);
				}
				return data.data.fileContent;
			}),
		)
	}

	//-----------------------------------------------------------------------------------------------
	loginUser$(body: any, params: Array<any>): Observable<any> {
	// Používá se jen pro lakovnu (jinde to jde přes funkcu saveData$)
	// ----------------------------------------------------------------------------------------------
		
		let reqType = ERequestTypes.PUT;

			const existTokens = this.setExistTokenForHeader();
			const request = this.dataHttpService.processServerRequest$(reqType, body, params, existTokens, this.timestamp).pipe(
			tap(rawData => {
				if (rawData.system['e2e-test'] === 'yes') {
					window['orax-e2e-server-system'] = rawData.system;
					window['orax-e2e-server-data'] = rawData.data;
				}
				return rawData;
			}),
		);
		return request;
	}

	//-----------------------------------------------------------------------------------------------
	logoutUser$(body: any, params: Array<any>): Observable<any> {
	// Používá se jen pro lakovnu (jinde to jde přes funkcu saveData$)
	// ----------------------------------------------------------------------------------------------
		
		let reqType = ERequestTypes.PUT;

			const existTokens = this.setExistTokenForHeader();
			const request = this.dataHttpService.processServerRequest$(reqType, body, params, existTokens, this.timestamp).pipe(
			tap(rawData => {
				if (rawData.system['e2e-test'] === 'yes') {
					window['orax-e2e-server-system'] = rawData.system;
					window['orax-e2e-server-data'] = rawData.data;
				}
				return rawData;
			}),
		);
		return request;
	}

	//-----------------------------------------------------------------------------------------------
	applyDefaults(defaults: object, data: object): object {
	// Používá se předplněný záznam (INSERTPREF) - nastavení vybraných položek na defaultní hodnoty
	// např. id na 0 apod.
	//-----------------------------------------------------------------------------------------------
		let updatedData = { ...data };
		for (const key in defaults) {
			if (key in data) {
				updatedData[key] = defaults[key]; // Přepis hodnoty defaultní
			}
		}
		return updatedData;
	}
	//-----------------------------------------------------------------------------------------------
}
